import { SPOTIFY_API_URL, SPOTIFY_AUTH_URL } from '@/constants/constants';
import type { TokenResponse, SpotifyErrorResponse, SpotifyPagingObject, SpotifyEpisode } from '@/types/spotify.types';

class SpotifyService {
  private baseUrl: string;
  private authUrl: string;
  private clientId: string | undefined;
  private clientSecret: string | undefined;
  private accessToken: string | null;
  private tokenExpiry: number;

  constructor() {
    this.baseUrl = SPOTIFY_API_URL;
    this.authUrl = SPOTIFY_AUTH_URL;
    this.clientId = import.meta.env.VITE_SPOTIFY_CLIENT_ID;
    this.clientSecret = import.meta.env.VITE_SPOTIFY_CLIENT_SECRET;
    this.accessToken = null;
    this.tokenExpiry = 0;
  }

  /**
   * Get a client credentials flow token from Spotify
   * @returns The access token
   */
  private async getClientCredentialsToken(): Promise<string> {
    if (!this.clientId || !this.clientSecret) {
      throw new Error('Spotify client ID and secret must be set in environment variables');
    }

    const params = new URLSearchParams();
    params.append('grant_type', 'client_credentials');

    const authString = btoa(`${this.clientId}:${this.clientSecret}`);

    const response = await fetch(this.authUrl, {
      method: 'POST',
      headers: {
        Authorization: `Basic ${authString}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params,
    });

    if (!response.ok) {
      throw new Error(`Failed to get Spotify access token: ${response.statusText}`);
    }

    const data = (await response.json()) as TokenResponse;
    this.accessToken = data.access_token;
    this.tokenExpiry = Date.now() + (data.expires_in - 60) * 1000;

    return data.access_token;
  }

  /**
   * Ensure we have a valid token before making requests
   * @returns A valid access token
   */
  private async ensureValidToken(): Promise<string> {
    if (!this.accessToken || Date.now() >= this.tokenExpiry) {
      return this.getClientCredentialsToken();
    }
    return this.accessToken;
  }

  /**
   * Make a request to the Spotify API
   * @param endpoint - The API endpoint to call
   * @param options - Additional fetch options
   * @returns The API response
   * @throws Error if the request fails
   */
  async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = await this.ensureValidToken();

    const url = `${this.baseUrl}${endpoint}`;
    const defaultOptions: RequestInit = {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    };

    const response = await fetch(url, { ...defaultOptions, ...options });

    if (!response.ok) {
      const errorData = (await response.json()) as SpotifyErrorResponse;
      throw new Error(`Spotify API error: ${response.status} ${errorData.error?.message || response.statusText}`);
    }

    return (await response.json()) as T;
  }

  /**
   * Fetch podcast episodes for a specific show/podcast
   * @param showId - The Spotify ID of the podcast/show
   * @param limit - The number of episodes to return (max 50)
   * @returns Object containing episodes and pagination info
   */
  async getPodcastEpisodes(showId: string, limit: number = 4): Promise<SpotifyPagingObject<SpotifyEpisode>> {
    let endpoint = `/shows/${showId}/episodes?limit=${limit}`;
    return this.makeRequest<SpotifyPagingObject<SpotifyEpisode>>(endpoint);
  }
}

const spotifyService = new SpotifyService();
export default spotifyService;
